/* eslint-disable complexity */
import {
  faDesktop,
  faFileContract,
  faQuestionCircle,
  faSchool,
  faShieldCheck,
  faSignOut,
  faUserCircle,
  faUserGear,
  faWrench,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useProfile } from '@/hooks';
import { buildLink, managerV1BaseUrl } from '@/utils';
import { Suspense } from 'react';
import { Badge } from '@/components/ui/badge';
import { WithFeatureFlags } from '@/features/featureFlags/WithFeatureFlags';
import {
  createSettingsLink,
  isMenuItemActive,
} from '@/domain/navigation/features/navigation/utils/helpers';
import { useHasUpgradableVertrag } from '@/domain/navigation/features/navigation/hooks/useVertraege/useHasUpgradableVertrag';
import { NewsFeedMenuItemFallback } from '@/domain/navigation/features/navigation/desktop/ProfileItemDropdown/NewsFeedSubMenu/NewsFeedMenuItemFallback';
import { NewsFeedSubMenu } from '@/domain/navigation/features/navigation/desktop/ProfileItemDropdown/NewsFeedSubMenu/NewsFeedSubMenu';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { WithLocationGuard } from '@/features/locationGuard/WithLocationGuard';

// eslint-disable-next-line max-lines-per-function
export function useDropdownMenuItems() {
  const { t } = useTranslation();
  const profile = useProfile();
  const location = useLocation();
  const { hasUpgradableVertrag } = useHasUpgradableVertrag();
  const manager = profile.manager;
  const menuItems = [];
  const { currentStandort } = useStandortContext();

  if (manager.rechte.admin) {
    menuItems.push(
      <DropdownMenuItem data-testid="menu-item-administration" asChild>
        <a
          className="w-full flex items-center gap-2"
          href={buildLink('allgemein')}>
          <FontAwesomeIcon icon={faUserGear} />
          {t('general.navigation.profileDropdown.administration')}
        </a>
      </DropdownMenuItem>,
    );
  }

  if (manager.hasEinstellungenRecht()) {
    const link = createSettingsLink(manager);
    menuItems.push(
      <DropdownMenuItem data-testid="menu-item-settings" asChild>
        <a className="w-full flex items-center gap-2" href={link}>
          <FontAwesomeIcon icon={faWrench} />
          {t('general.navigation.profileDropdown.settings')}
        </a>
      </DropdownMenuItem>,
    );
  }

  if (
    manager.rechte.admin &&
    (currentStandort.land === 'AT' || currentStandort.land === 'DE')
  ) {
    menuItems.push(
      <DropdownMenuItem
        active={isMenuItemActive('vertraege', location.pathname)}
        data-testid="menu-item-vertraege"
        asChild>
        <Link
          className="w-full flex items-center flex-nowrap gap-2"
          to="/vertraege">
          <FontAwesomeIcon icon={faFileContract} />
          <span className="whitespace-nowrap">
            {t('general.navigation.profileDropdown.contracts')}
          </span>
          {hasUpgradableVertrag && (
            <WithFeatureFlags name="vertraege-2" Fallback={<></>}>
              <Badge type="pill" variant="secondary-indigo">
                Upgrade
              </Badge>
            </WithFeatureFlags>
          )}
        </Link>
      </DropdownMenuItem>,
    );
  }
  menuItems.push(
    <DropdownMenuItem
      active={isMenuItemActive('profile', location.pathname)}
      data-testid="menu-item-profile"
      asChild>
      <Link className="w-full flex items-center gap-2" to="/profile">
        <FontAwesomeIcon icon={faUserCircle} />
        {t('general.navigation.profileDropdown.myProfile')}
      </Link>
    </DropdownMenuItem>,
  );
  if (manager.rechte.admin || manager.hasEinstellungenRecht()) {
    menuItems.push(<DropdownMenuSeparator />);
  }

  menuItems.push(
    <DropdownMenuItem data-testid="menu-item-privacy" asChild>
      <Suspense fallback={<NewsFeedMenuItemFallback />}>
        <NewsFeedSubMenu />
      </Suspense>
    </DropdownMenuItem>,
  );

  menuItems.push(
    <WithFeatureFlags name="softwarekurs">
      <WithLocationGuard countries={['DE', 'AT']}>
        <DropdownMenuItem data-testid="menu-item-privacy" asChild>
          <a
            className="w-full flex items-center gap-2"
            target="__blank"
            href="https://training.e2n.de/">
            <FontAwesomeIcon icon={faSchool} />
            {t('general.navigation.profileDropdown.softwarekurs')}
            <Badge type="pill" variant="secondary-indigo">
              Neu
            </Badge>
          </a>
        </DropdownMenuItem>
      </WithLocationGuard>
    </WithFeatureFlags>,
  );

  //TODO: Nicht anzeigen bei Schweizer mandant
  menuItems.push(
    <DropdownMenuItem data-testid="menu-item-privacy" asChild>
      <a
        className="w-full flex items-center gap-2"
        href={buildLink('datenschutz')}>
        <FontAwesomeIcon icon={faShieldCheck} />
        {t('general.navigation.profileDropdown.privacy')}
      </a>
    </DropdownMenuItem>,
  );

  menuItems.push(
    <DropdownMenuItem data-testid="menu-item-help" asChild>
      <a
        className="w-full flex items-center gap-2"
        href="https://hilfe.e2n.de"
        target="_blank"
        rel="noreferrer">
        <FontAwesomeIcon icon={faQuestionCircle} />
        {t('general.navigation.profileDropdown.help')}
      </a>
    </DropdownMenuItem>,
  );

  menuItems.push(
    <DropdownMenuItem data-testid="menu-item-remoteMaintenance" asChild>
      <a
        className="w-full flex items-center gap-2"
        href="https://get.teamviewer.com/e2ngastro"
        target="_blank"
        rel="noreferrer">
        <FontAwesomeIcon icon={faDesktop} />
        {t('general.navigation.profileDropdown.remoteMaintenance')}
      </a>
    </DropdownMenuItem>,
  );

  menuItems.push(<DropdownMenuSeparator />);
  menuItems.push(
    <DropdownMenuItem
      className="flex items-center gap-2"
      data-testid="navigation-avatar-dropdown-logout"
      onClick={() => {
        window.location.href = `${managerV1BaseUrl}/logout`;
      }}>
      <FontAwesomeIcon icon={faSignOut} />
      <span>{t('general.navigation.profileDropdown.logout')}</span>
    </DropdownMenuItem>,
  );
  return menuItems;
}
